import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./assest_design/Innovation.css";

import wwdash from "./assets/wwdash.png";
import wwgeo from "./assets/wwgeo.png";
import hydro1 from "./assets/hydro1.png";
import hydro2 from "./assets/hydro2.JPG";
import rural1 from "./assets/rural1.jpg";
import rural2 from "./assets/rural2.jpg";

export default function Innovation() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title =
      "Innovation Projects | Custom Software, AI, IoT & Research Platforms in South Africa | ProCode ZA";

    const setMeta = (name, content, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;

      let tag = document.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        if (property) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMeta(
      "description",
      "Explore ProCode ZA innovation projects across custom software development, AI, IoT, dashboard systems, education technology, mining operations and research platform development in Johannesburg, Cape Town, Durban and across South Africa."
    );
    setMeta(
      "keywords",
      "custom software development South Africa, AI development South Africa, IoT development South Africa, dashboard development South Africa, education technology South Africa, research management platform South Africa, mining software South Africa, Johannesburg software company, Cape Town software developers, Durban app developers, ProCode ZA innovation"
    );
    setMeta(
      "og:title",
      "Innovation Projects | Custom Software, AI, IoT and Research Platforms in South Africa | ProCode ZA",
      true
    );
    setMeta(
      "og:description",
      "Real client and innovation projects built by ProCode ZA across mining, hydroponics, education, rural technology and research platform development in South Africa.",
      true
    );
    setMeta("og:type", "website", true);
  }, []);

  const innovationProjects = [
    {
      id: "mining",
      title: "Mining Geolocation Tracker Application",
      subtitle: "Mining operations software, route visibility and workforce oversight",
      description: [
        "A custom mobile and web-based workforce tracking system developed for locomotive inspection teams operating in demanding mining environments.",
        "The platform captures employee movement, inspection paths, time-on-site activity and operational location data in real time, while the web dashboard gives managers centralised visibility over routes, reporting, compliance and field productivity.",
        "This solution reflects ProCode ZA’s strength in building commercial software for operational teams that need reliability, visibility and accountability in high-pressure environments."
      ],
      tags: ["Custom Software", "Mining Operations", "Live Dashboard", "Geolocation"],
      results: [
        "Live monitoring",
        "Route tracking",
        "Admin dashboard",
        "Compliance visibility"
      ],
      quote:
        "The geolocation tracker is excellent. It’s reliable, easy to use, and we’re excited to continue doing more business together.",
      images: [
        { src: wwgeo, alt: "Mining workforce geolocation tracking application by ProCode ZA" },
        { src: wwdash, alt: "Mining operations dashboard and reporting platform by ProCode ZA" }
      ],
      reverse: false
    },
    {
      id: "hydroponics",
      title: "Hybrid AI Hydroponics System for Rural Africa",
      subtitle: "AI, offline-first agriculture technology and smart food systems",
      description: [
        "An intelligent hydroponics solution designed for rural African environments where infrastructure can be limited, connectivity is inconsistent and resilience matters.",
        "The system combines AI-assisted monitoring, nutrient optimisation, pH tracking and predictive growth modelling to support sustainable food production in both online and offline conditions.",
        "This project shows how ProCode ZA can develop applied AI, smart agriculture systems, embedded intelligence and practical innovation for real-world African contexts."
      ],
      tags: ["AI Development", "IoT", "Smart Agriculture", "Offline Systems"],
      results: [
        "Offline-ready",
        "AI monitoring",
        "Growth prediction",
        "Smart nutrient control"
      ],
      quote:
        "The Hybrid AI Hydroponics platform has been running reliably for over 4 years now. It’s transformed how we grow food.",
      images: [
        { src: hydro1, alt: "Hybrid AI hydroponics software interface by ProCode ZA" },
        { src: hydro2, alt: "Smart hydroponics system for rural Africa developed by ProCode ZA" }
      ],
      reverse: true
    },
    {
      id: "prism",
      title: "Emeris Research Platform (PRISM)",
      subtitle: "Research management platform, postgraduate workflows and academic operations",
      description: [
        "PRISM is a purpose-built research platform created to support postgraduate programme operations, student-supervisor workflows and academic administration in a structured digital environment.",
        "The platform brings together role-based access, submissions, MOU handling, supervision visibility, progress tracking, notices, ticketing and administrative oversight into a single system designed for academic institutions.",
        "Built for Emeris, PRISM demonstrates ProCode ZA’s capability in developing education technology, secure workflow platforms, document-driven systems and admin dashboards that support large-scale programme coordination."
      ],
      tags: ["EdTech", "Research Platform", "Admin Dashboard", "Workflow System"],
      results: [
        "Role-based access",
        "Supervisor workflows",
        "Admin oversight",
        "Submission tracking"
      ],
      quote:
        "PRISM was designed to improve visibility, structure and coordination across the research journey for students, supervisors and administrators.",
      images: [
        { src: rural1, alt: "PRISM research management and supervision workflow platform by ProCode ZA" },
        { src: rural2, alt: "PRISM academic administration and postgraduate research platform interface" }
      ],
      reverse: false
    },
    {
      id: "learning",
      title: "Rural Mother & Kids Learning Platform",
      subtitle: "Education technology for accessibility, literacy and community impact",
      description: [
        "A digital learning platform developed to support rural mothers and children through accessible, culturally relevant educational content.",
        "The solution focuses on literacy, numeracy and early development while remaining practical for low-connectivity environments and communities that need simple, dependable access.",
        "This project reflects ProCode ZA’s ability to create human-centred education technology that balances accessibility, local relevance and meaningful long-term use."
      ],
      tags: ["Education Technology", "Community Impact", "Low Connectivity", "Learning Platform"],
      results: [
        "Low-bandwidth friendly",
        "Accessible learning",
        "Community-centred",
        "Early education support"
      ],
      quote:
        "Le nkqubo isincedile kakhulu. Abantwana bethu bayafunda, kuthi oomama kusenze sazinza ngakumbi.",
      images: [
        { src: rural1, alt: "Rural learning platform for mothers and children by ProCode ZA" },
        { src: rural2, alt: "Accessible education technology platform developed by ProCode ZA" }
      ],
      reverse: true
    }
  ];

  return (
    <div className="innovation-page">
      <section className="innovation-header">
        <div className="innovation-badge">Innovation portfolio</div>

        <h1>Client & Community Innovation Projects</h1>

        <p>
          ProCode ZA develops commercial-grade digital systems for businesses,
          institutions and programmes in Johannesburg, Cape Town, Durban and
          across South Africa that need more than a basic website. From mining
          software and smart agriculture to education platforms, AI systems and
          research workflow tools, our innovation work is built to solve
          practical problems and support long-term growth.
        </p>

        <div className="innovation-header-actions">
          <button
            className="innovation-btn primary"
            onClick={() => navigate("/proquote")}
            type="button"
          >
            Get a ProQuote
          </button>

          <button
            className="innovation-btn secondary"
            onClick={() => navigate("/contact")}
            type="button"
          >
            Start a Project
          </button>
        </div>

        <div className="innovation-trust-row">
          <span>Custom software development</span>
          <span>AI and machine learning solutions</span>
          <span>IoT and smart systems</span>
          <span>Education and research platforms</span>
        </div>
      </section>

      {innovationProjects.map((project) => (
        <section
          key={project.id}
          id={project.id}
          className={`innovation-project ${project.reverse ? "alt" : ""}`}
        >
          <div className="project-text">
            <div className="project-kicker">{project.subtitle}</div>
            <h2>{project.title}</h2>

            <div className="project-tags">
              {project.tags.map((tag) => (
                <span key={tag} className="project-tag">
                  {tag}
                </span>
              ))}
            </div>

            <div className="project-results-strip">
              {project.results.map((item) => (
                <span key={item} className="project-result-pill">
                  {item}
                </span>
              ))}
            </div>

            {project.description.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}

            <blockquote>{project.quote}</blockquote>
          </div>

          <div className="project-images">
            {project.images.map((image, index) => (
              <img key={index} src={image.src} alt={image.alt} />
            ))}
          </div>
        </section>
      ))}

      <section className="innovation-services-strip">
        <h2>Beyond innovation projects, we also support delivery across</h2>

        <div className="services-mini-grid">
          <div className="mini-service-card">Business websites and landing pages</div>
          <div className="mini-service-card">Dashboards and admin systems</div>
          <div className="mini-service-card">Branding and digital creative support</div>
          <div className="mini-service-card">AI and machine learning development</div>
          <div className="mini-service-card">IoT and intelligent hardware concepts</div>
          <div className="mini-service-card">Programme coordination support at scale</div>
        </div>
      </section>

      <section className="innovation-cta">
        <h2>Looking for a South African software team that can build beyond the ordinary?</h2>

        <p>
          Whether you need a custom business platform, a research management
          system, an AI-powered workflow, a smart IoT concept, a learning
          platform or a branded digital experience, ProCode ZA can help shape,
          build and support the right solution for teams in Johannesburg, Cape
          Town, Durban and across South Africa.
        </p>

        <div className="innovation-cta-actions">
          <button
            className="innovation-btn primary"
            onClick={() => navigate("/pod-programme")}
            type="button"
          >
            Join the Pod
          </button>

          <button
            className="innovation-btn secondary"
            onClick={() => navigate("/proquote")}
            type="button"
          >
            Get a ProQuote
          </button>
        </div>
      </section>
    </div>
  );
}